import React, { useEffect, useState, useRef } from 'react';
import { Coins, Bell, TrendingDown, TrendingUp, ChevronRight } from 'lucide-react';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { supabase } from '../lib/supabase';
import { formatDateColombia } from '../utils/dateFormat';
import CreditHistoryModal from './CreditHistoryModal';

interface CreditTransaction {
  id: string;
  amount: number;
  description: string;
  points_count: number;
  created_at: string;
  is_read: boolean;
}

const CreditDisplay: React.FC = () => {
  const profile = useSelector((state: RootState) => state.auth.profile);
  const credits = profile?.credits;
  const user = useSelector((state: RootState) => state.auth.user);
  const [recentTransactions, setRecentTransactions] = useState<CreditTransaction[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Ocultar completamente el widget de créditos para panel admin (roles distintos a cliente)
  const isClientRole = profile?.role === 'user' || profile?.role === 'cliente';

  useEffect(() => {
    if (user) {
      fetchRecentTransactions();
      // Actualizar cada 30 segundos para obtener nuevas notificaciones
      const interval = setInterval(() => {
        fetchRecentTransactions();
      }, 30000);
      
      return () => clearInterval(interval);
    }
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  const fetchRecentTransactions = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      // Obtener todas las transacciones (incluyendo leídas) para el dropdown
      const { data, error } = await supabase()
        .from('credit_transactions')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;
      setRecentTransactions((data || []) as CreditTransaction[]);

      // Contar transacciones no leídas para el badge
      const { count, error: countError } = await supabase()
        .from('credit_transactions')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .eq('is_read', false);

      if (countError) throw countError;
      setUnreadCount(count || 0);
    } catch (error) {
      console.error('Error al obtener transacciones recientes:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async () => {
    if (!user) return;

    try {
      // Marcar TODAS las transacciones no leídas del usuario como leídas
      // No solo las que están en recentTransactions, sino todas
      const { error } = await supabase()
        .from('credit_transactions')
        .update({ is_read: true })
        .eq('user_id', user.id)
        .eq('is_read', false);

      if (error) {
        console.error('Error al marcar notificaciones como leídas:', error);
        throw error;
      }

      // Actualizar el estado local inmediatamente
      setRecentTransactions(prev => 
        prev.map(t => ({ ...t, is_read: true }))
      );
      setUnreadCount(0);

      // Refrescar la lista para asegurar sincronización
      await fetchRecentTransactions();
    } catch (error) {
      console.error('Error al marcar notificaciones como leídas:', error);
    }
  };

  // Formatear créditos (número con separadores de miles)
  const formatearCreditos = (valor: number) => {
    return new Intl.NumberFormat('es-CO').format(valor);
  };

  if (!user || !isClientRole) {
    return null;
  }

  return (
    <>
      <div className="flex items-center gap-3 relative">
        <div className="flex items-center gap-1">
          <Coins className="w-4 h-4 text-yellow-500" />
          <span className="text-sm font-medium">
            {credits === null || credits === undefined ? '...' : formatearCreditos(credits)}
          </span>
        </div>
        
        {/* Botón de campanita con dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={async () => {
              const wasOpen = showDropdown;
              setShowDropdown(!showDropdown);
              if (!wasOpen) {
                // Marcar como leídas cuando se abre el dropdown (después de cambiar el estado)
                // Usar un pequeño delay para asegurar que el estado se actualice
                setTimeout(() => {
                  markAsRead();
                }, 100);
              }
            }}
            className="relative p-2 text-gray-400 hover:text-white transition-colors"
            title="Ver movimientos financieros"
          >
            <Bell size={20} />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {/* Dropdown con últimos 10 consumos */}
          {showDropdown && (
            <div className="absolute right-0 top-full mt-2 w-80 bg-[#1F2937] rounded-lg shadow-xl border border-gray-700 z-50 max-h-96 overflow-hidden flex flex-col">
              <div className="p-4 border-b border-gray-700 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-white">Últimos movimientos</h3>
                <button
                  onClick={() => {
                    setShowDropdown(false);
                    setShowModal(true);
                  }}
                  className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
                >
                  Ver todo
                </button>
              </div>
              
              <div className="overflow-y-auto max-h-80">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                  </div>
                ) : recentTransactions.length === 0 ? (
                  <div className="text-gray-400 text-xs text-center py-8">
                    No hay transacciones recientes
                  </div>
                ) : (
                  <div className="divide-y divide-gray-700">
                    {recentTransactions.map((transaction) => {
                      const isRecarga = transaction.amount > 0;
                      return (
                      <div
                        key={transaction.id}
                        className="p-3 hover:bg-gray-800/50 transition-colors cursor-pointer"
                        onClick={() => {
                          setShowDropdown(false);
                          setShowModal(true);
                        }}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start space-x-2 flex-1 min-w-0">
                            {isRecarga ? (
                              <TrendingUp size={14} className="mt-0.5 flex-shrink-0 text-green-400" />
                            ) : (
                              <TrendingDown size={14} className="mt-0.5 flex-shrink-0 text-red-400" />
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-gray-300 truncate">
                                {transaction.description}
                              </p>
                              <p className="text-xs text-gray-500 mt-0.5">
                                {formatDateColombia(transaction.created_at)}
                              </p>
                            </div>
                          </div>
                          <span className={`text-xs font-semibold flex-shrink-0 ml-2 ${
                            isRecarga ? 'text-green-400' : 'text-red-400'
                          }`}>
                            {isRecarga ? '+' : '-'}{formatearCreditos(Math.abs(transaction.amount))}
                          </span>
                        </div>
                      </div>
                    );})}
                  </div>
                )}
              </div>

              {recentTransactions.length > 0 && (
                <div className="p-3 border-t border-gray-700">
                  <button
                    onClick={() => {
                      setShowDropdown(false);
                      setShowModal(true);
                    }}
                    className="w-full flex items-center justify-center space-x-2 text-xs text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    <span>Ver todos los movimientos</span>
                    <ChevronRight size={14} />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal de historial completo */}
      {user && (
        <CreditHistoryModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          userId={user.id}
        />
      )}
    </>
  );
};

export default CreditDisplay;